import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Preference } from '../enums/preference.enum';

export interface IBaseEmailForm {
	email1: FormControl<string | null>;
	email2: FormControl<string | null>;
	email3: FormControl<string | null>;
}

export interface IReleveAffaireForm extends IBaseEmailForm {
	envoieAutomatique: FormControl<boolean | false>;
}

export interface INotificationPreferenceForm extends IBaseEmailForm {
	preferences: FormArray<
		FormGroup<{
			code: FormControl<string>;
			label: FormControl<string>;
			actif: FormControl<boolean>;
			valeur: FormControl<string | null>;
		}>
	>;
}

export interface IPreferenceModeReceptionForm {
	retour: FormControl<string | null>;
}

export interface IPreference {
	actif: boolean;
	code: string;
	valeur?: string | null;
	utilisateurModification: string;
	dateModification: Date;
}

export class PreferencesItem {
	public code: string;
	public label: string;
	public actif: boolean;
	public valeur?: string | null;

	public utilisateurModification?: string | null;
	public dateModification?: Date | null;

	constructor(code: string, label: string, preference?: IPreference) {
		this.code = code;
		this.label = label;

		if (preference) {
			this.actif = preference.actif;
			this.valeur = preference.valeur;
			this.utilisateurModification = preference.utilisateurModification;
			this.dateModification = preference.dateModification;
		} else {
			this.actif = false;
			this.valeur = null;
		}
	}
}

export interface IEditPreferences {
	category: string;
	preferences: IPreference[];
}

export abstract class PreferencesGroup {
	protected items: PreferencesItem[] = [];

	constructor(preferences: IPreference[], config: { [key: string]: { code: string; label: string } }) {
		const map = new Map(preferences.map(p => [p.code, p]));

		for (const key in config) {
			const { code, label } = config[key];
			const item = new PreferencesItem(code, label, map.get(code));
			this.items.push(item);
			(this as any)[key] = item;
		}
	}

	public get lastDateModification(): Date | null {
		return this.getLatestModifiedItem()?.dateModification ?? null;
	}

	public get lastUtilisateurModification(): string | null {
		return this.getLatestModifiedItem()?.utilisateurModification ?? null;
	}

	private getLatestModifiedItem(): PreferencesItem | null {
		return this.items
			.filter(i => i.dateModification != null)
			.reduce(
				(latest, current) => (!latest || current.dateModification! > latest.dateModification! ? current : latest),
				null as PreferencesItem | null
			);
	}
}

export class Cheque extends PreferencesGroup {
	public email1: PreferencesItem;
	public email2: PreferencesItem;
	public email3: PreferencesItem;

	constructor(preferences: IPreference[]) {
		super(preferences, Preference.cheques);
	}
}

export class PreferenceContact extends PreferencesGroup {
	public email: PreferencesItem;
	public telephone: PreferencesItem;

	constructor(preferences: IPreference[]) {
		super(preferences, Preference.contact);
	}
}

export class PreferenceModeReception extends PreferencesGroup {
	public retour: PreferencesItem;

	constructor(preferences: IPreference[]) {
		super(preferences, Preference.modeReception);
	}
}

export class PreferencesNotifications extends PreferencesGroup {
	public depots: PreferencesItem;
	public ribsDelivres: PreferencesItem;
	public virementsCredites: PreferencesItem;
	public reglementsCheques: PreferencesItem;
	public reglementsVirements: PreferencesItem;
	public operationsRenvoyesCarpa: PreferencesItem;
	public OppositionChequesEmis: PreferencesItem;
	public email1: PreferencesItem;
	public email2: PreferencesItem;
	public email3: PreferencesItem;

	constructor(preferences: IPreference[]) {
		super(preferences, Preference.notifications);
	}

	public toList(): PreferencesItem[] {
		return [
			this.depots,
			this.ribsDelivres,
			this.virementsCredites,
			this.reglementsCheques,
			this.reglementsVirements,
			this.operationsRenvoyesCarpa,
			this.OppositionChequesEmis
		];
	}
}

export class ReleveAffaire extends PreferencesGroup {
	public email1: PreferencesItem;
	public email2: PreferencesItem;
	public email3: PreferencesItem;
	public envoieAutomatique: PreferencesItem;

	constructor(preferences: IPreference[]) {
		super(preferences, Preference.releveaffaires);
	}
}
